import { useState } from 'react'
import { LayerSummary } from 'types'
import { Button } from 'components/Button/Button'
import Styles from './Modal.module.scss'
import { CloseReviewActionConfig } from 'stores/Workflows/Types'
import { pluralize } from 'utils'
import { MessageField } from '../MessageField/MessageField'
import { Data, useMergeLayer } from 'hooks/queries/layers/useMergeLayer'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Modal, useModals } from 'almanac-ui/Modal'

interface BaseProps {
  layer: LayerSummary
  onComplete?: (data: Data) => void
  payload?: Partial<CloseReviewActionConfig>
  onMergeSuccess?: VoidFunction
  includedChanges?: number
}

interface SelectiveMerge extends BaseProps {
  includedUuids: string[]
  includedCommentIds: number[]
  suggestionsCount: number
  commentsCount: number
}

interface CompleteMerge extends BaseProps {
  suggestionsCount: number
  commentsCount: number
  includedUuids?: undefined
  includedCommentIds?: undefined
}

type Props = CompleteMerge | SelectiveMerge

export const MergeLayerModal = ({
  layer,
  onComplete,
  suggestionsCount,
  commentsCount,
  includedUuids,
  includedCommentIds,
  payload,
  onMergeSuccess,
  includedChanges = 0,
}: Props) => {
  const { mutate: mergeAndDeleteLayer, isLoading: isMergingAndDeleting } = useMergeLayer(
    { articleKey: layer.article_id, id: layer.id },
    {
      onError: (error) => {
        if (error instanceof Error) {
          showErrorMessageToast(error.message)
          return
        }
        showErrorMessageToast('There was a problem merging the layer, our team has been notified.')
      },
      onSuccess: (data) => {
        onMergeSuccess?.()
        onComplete?.(data)
      },
    },
  )
  const [htmlMessage, setHtmlMessage] = useState('')
  const { closeModal } = useModals()

  return (
    <Modal.Root>
      <Modal.Header title="Leave a merge note" size="sm" />
      <Modal.Content>
        <p className={Styles.description}>
          {suggestionsCount} {pluralize('change', suggestionsCount)} and {commentsCount}{' '}
          {pluralize('comment', commentsCount)} are going to be merged and the owner of the doc will be notified.
        </p>
        <div className={Styles.messageField}>
          <MessageField
            placeholder="Type your message here..."
            value={htmlMessage}
            onChange={(htmlString) => setHtmlMessage(htmlString)}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="xl" isDisabled={isMergingAndDeleting} onPress={() => closeModal()}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            variant="primary"
            onPress={() =>
              mergeAndDeleteLayer({ payload, includedChanges, note: htmlMessage, includedUuids, includedCommentIds })
            }
            size="xl"
            isDisabled={isMergingAndDeleting}
          >
            Merge {suggestionsCount} {pluralize('Change', suggestionsCount)}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
